import { Link } from 'react-router-dom'
import { UserButton, useAuth } from '@clerk/clerk-react'
import { useEffect, useMemo, useState } from 'react'

type Statement = {
  id: string
  filename: string
  status: 'uploaded' | 'processing' | 'completed' | 'failed'
  uploaded_at: number
  processed_at?: number | null
}

export default function DashboardPage() {
  const { getToken } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statements, setStatements] = useState<Statement[]>([])
  const [me, setMe] = useState<any>(null)
  const [exportingId, setExportingId] = useState<string | null>(null)
  const [reprocessingId, setReprocessingId] = useState<string | null>(null)

  const apiBase = useMemo(() => import.meta.env.VITE_API_URL || '/api/v1', [])

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const [meRes, listRes] = await Promise.all([
        fetch(`${apiBase}/me`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiBase}/statements`, { headers: { Authorization: `Bearer ${token}` } }),
      ])

      if (!meRes.ok) throw new Error(await meRes.text())
      if (!listRes.ok) throw new Error(await listRes.text())

      const meJson = await meRes.json()
      const listJson = await listRes.json()
      setMe(meJson.user)
      setStatements(listJson.statements || [])
    } catch (e: any) {
      setError(e.message || 'Failed to load dashboard')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-refresh while any statement is processing
  useEffect(() => {
    const hasProcessing = statements.some((s) => s.status === 'processing')
    if (!hasProcessing) return
    const t = setInterval(() => {
      fetchData()
    }, 5000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statements])

  const formatDate = (ts?: number | null) => {
    if (!ts) return '-'
    const d = new Date((ts as number) * 1000)
    return d.toISOString().slice(0, 10)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this statement? This cannot be undone.')) return
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const res = await fetch(`${apiBase}/statements/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(await res.text())
      await fetchData()
    } catch (e: any) {
      alert(`Delete failed: ${e.message}`)
    }
  }

  const triggerExport = async (id: string, format: 'csv' | 'xlsx' | 'json') => {
    try {
      setExportingId(id)
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const res = await fetch(`${apiBase}/statements/${id}/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ format }),
      })
      if (!res.ok) throw new Error(await res.text())
      // Download
      const dl = await fetch(`${apiBase}/statements/${id}/export/${format}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!dl.ok) throw new Error(await dl.text())
      const blob = await dl.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `statement-${id}.${format}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      alert(`Export failed: ${e.message}`)
    } finally {
      setExportingId(null)
    }
  }

  const triggerReprocess = async (id: string) => {
    try {
      setReprocessingId(id)
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const res = await fetch(`${apiBase}/statements/${id}/process`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(await res.text())
      await fetchData()
    } catch (e: any) {
      alert(`Re-process failed: ${e.message}`)
    } finally {
      setReprocessingId(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-indigo-600">
              BankStatementReader
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="px-4 py-2 text-gray-700">
                Dashboard
              </Link>
              <UserButton />
            </div>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage your bank statements</p>
        </div>

        {/* Upload CTA */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-3xl">📄</div>
              <div>
                <h2 className="text-xl font-bold">Upload a Statement</h2>
                <p className="text-gray-600">Drag and drop a PDF on the upload page</p>
              </div>
            </div>
            <Link
              to="/upload"
              className="inline-block px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Upload
            </Link>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded">{error}</div>
        )}

        {/* Statements List */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Recent Statements</h2>
            <button onClick={fetchData} className="text-sm text-indigo-600 hover:text-indigo-700">Refresh</button>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : statements.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No statements yet. Upload your first statement to get started!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead>
                  <tr className="text-gray-600 text-sm">
                    <th className="py-2 px-3">Filename</th>
                    <th className="py-2 px-3">Uploaded</th>
                    <th className="py-2 px-3">Processed</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {statements.map((s) => (
                    <tr key={s.id} className="border-t">
                      <td className="py-2 px-3">
                        <Link to={`/statements/${s.id}`} className="text-indigo-600 hover:text-indigo-700">
                          {s.filename}
                        </Link>
                      </td>
                      <td className="py-2 px-3">{formatDate(s.uploaded_at)}</td>
                      <td className="py-2 px-3">{formatDate(s.processed_at)}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          s.status === 'completed' ? 'bg-green-100 text-green-700' :
                          s.status === 'failed' ? 'bg-red-100 text-red-700' :
                          s.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2 justify-end">
                          {s.status === 'processing' && (
                            <div className="flex items-center gap-3 mr-2 text-yellow-700 w-40">
                              <div className="h-3 w-3 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
                              <div className="flex-1 h-2 bg-yellow-100 rounded overflow-hidden">
                                <div className="h-2 bg-yellow-400 animate-pulse w-full" />
                              </div>
                            </div>
                          )}
                          {(s.status === 'uploaded' || s.status === 'failed') && (
                            <button
                              onClick={() => triggerReprocess(s.id)}
                              disabled={reprocessingId === s.id}
                              className={`px-2 py-1 text-xs rounded ${reprocessingId === s.id ? 'bg-gray-200 text-gray-500' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                            >
                              {reprocessingId === s.id ? 'Re-processing…' : 'Re-process'}
                            </button>
                          )}
                          <button
                            onClick={() => triggerExport(s.id, 'csv')}
                            disabled={exportingId === s.id || s.status !== 'completed'}
                            className={`px-2 py-1 text-xs rounded ${s.status !== 'completed' ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 hover:bg-gray-200'}`}
                          >
                            CSV
                          </button>
                          <button
                            onClick={() => triggerExport(s.id, 'xlsx')}
                            disabled={exportingId === s.id || s.status !== 'completed'}
                            className={`px-2 py-1 text-xs rounded ${s.status !== 'completed' ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 hover:bg-gray-200'}`}
                          >
                            XLSX
                          </button>
                          <button
                            onClick={() => triggerExport(s.id, 'json')}
                            disabled={exportingId === s.id || s.status !== 'completed'}
                            className={`px-2 py-1 text-xs rounded ${s.status !== 'completed' ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 hover:bg-gray-200'}`}
                          >
                            JSON
                          </button>
                          <button
                            onClick={() => handleDelete(s.id)}
                            className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Usage Stats */}
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-1">Statements This Month</div>
            <div className="text-3xl font-bold text-indigo-600">{me ? `${me.statements_this_month} / ${me.statementLimit}` : '—'}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-1">Current Plan</div>
            <div className="text-3xl font-bold text-green-600">{me?.plan || '—'}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-1">Member Since</div>
            <div className="text-3xl font-bold text-gray-900">{me ? formatDate(me.created_at) : '—'}</div>
          </div>
        </div>
      </main>
    </div>
  )
}
